'use client';

import { useState, useEffect, useRef } from "react";
import type { LanguageExtractionResult, TranslateRequest, TranslationContext } from "../api/translate/route";
import { Button } from "@/components/ui/button";
import LangDropdown, { SelectedLanguage } from "@/components/translate/lang-dropdown";
import { LanguageVarietyScriptPicker } from "@/components/translate/language-variety-script-picker";
import { ArrowRightLeft } from "lucide-react";
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from "sonner";
import { useTranslations, useLocale } from "next-intl";
import { InputBox } from "@/components/translate/input-box";
import { OutputBox } from "@/components/translate/output-box";
import { RTLProvider } from "@/components/translate/rtl-context";
import { AnonymousLimitDialog } from "@/components/translate/anonymous-limit-dialog";
import { RegisteredLimitDialog } from "@/components/translate/registered-limit-dialog";
import { TranslationHistory } from "@/components/translate/translation-history";
import { ConsentModal } from "@/components/consent-modal";
import { InstallPrompt } from "@/components/install-prompt";
import { useDynamicFontSize } from "@/utils/hooks/useDynamicFontSize";
//import { DebugUsagePanel } from "@/components/debug-usage-panel";
import { historyService, type HistoryEntry } from "@/lib/history-service";
import { getLanguages } from "@/app/[locale]/actions/languages";
import { updateUserSettings } from "@/app/[locale]/actions/settings";
import { SINGLE_TRANSLATION_LIMITS, STARKY_SENSE_LIMITS } from "@/lib/constants";
import { starkySenseService } from "@/lib/starky-sense-service";
import { StarkySenseDialog } from "@/components/translate/starky-sense-dialog";
import { StarkySenseRequest, StarkySenseResult, StarkySenseSolution } from "../api/starky-sense/route";
import { useSearchParams } from "next/navigation";

interface TranslationCreditLimitInfo {
  canTranslate: boolean;
  availableCharacters: number;
  attemptedCharacters: number;
  insufficientBy: number;
  isAnonymous: boolean;
}

interface CheckoutPrepareRedirectDetail {
  restoreKey: string;
}

interface CheckoutDraftPayload {
  version: 1;
  savedAt: number;
  inputText: string;
  sourceLang: SelectedLanguage | null;
  targetLang: SelectedLanguage | null;
  targetVarietyId: string | null;
  targetScriptId: string | null;
  translationContext: TranslationContext;
  tempImageContext: string | null;
}

const CHECKOUT_PREPARE_REDIRECT_EVENT = 'checkout-prepare-redirect';
const CHECKOUT_DRAFT_STORAGE_PREFIX = 'checkout-draft:';
const CHECKOUT_DRAFT_TTL_MS = 1000 * 60 * 60 * 2;

function sanitizeRestoreKey(rawRestoreKey: string | null | undefined): string | null {
  if (!rawRestoreKey) {
    return null;
  }

  const trimmed = rawRestoreKey.trim();
  if (trimmed.length === 0 || trimmed.length > 120) {
    return null;
  }

  if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) {
    return null;
  }

  return trimmed;
}

function getCheckoutDraftStorageKey(restoreKey: string): string {
  return `${CHECKOUT_DRAFT_STORAGE_PREFIX}${restoreKey}`;
}

function pruneExpiredCheckoutDrafts(): void {
  const now = Date.now();

  for (let i = localStorage.length - 1; i >= 0; i -= 1) {
    const storageKey = localStorage.key(i);
    if (!storageKey || !storageKey.startsWith(CHECKOUT_DRAFT_STORAGE_PREFIX)) {
      continue;
    }

    const rawPayload = localStorage.getItem(storageKey);
    if (!rawPayload) {
      continue;
    }

    try {
      const parsed = JSON.parse(rawPayload) as Partial<CheckoutDraftPayload>;
      if (!parsed.savedAt || typeof parsed.savedAt !== 'number') {
        localStorage.removeItem(storageKey);
        continue;
      }

      if (now - parsed.savedAt > CHECKOUT_DRAFT_TTL_MS) {
        localStorage.removeItem(storageKey);
      }
    } catch {
      localStorage.removeItem(storageKey);
    }
  }
}

export default function Home() {
  const langT = useTranslations('langDropdown');
  const [inputText, setInputText] = useState('');
  const [lastTranslatedText, setLastTranslatedText] = useState('');
  const [sourceLang, setSourceLang] = useState<SelectedLanguage>(undefined);
  const [targetLang, setTargetLang] = useState<SelectedLanguage>(undefined);
  const [lastSourceLang, setLastSourceLang] = useState<SelectedLanguage>(undefined);
  const [lastTargetLang, setLastTargetLang] = useState<SelectedLanguage>(undefined);
  const [outputText, setOutputText] = useState('');
  const [pendingTranslations, setPendingTranslations] = useState(0);
  const currentRequestController = useRef<AbortController | null>(null);
  const [transAnalysis, setTransAnalysis] = useState<LanguageExtractionResult | null>(null);
  const [translationContext, setTranslationContext] = useState<TranslationContext>({});
  const [lastTranslationContext, setLastTranslationContext] = useState<TranslationContext>({});
  const [tempImageContext, setTempImageContext] = useState<string | undefined>(undefined);

  // Language variety and script selection
  const [targetVarietyId, setTargetVarietyId] = useState<string | undefined>(undefined);
  const [targetScriptId, setTargetScriptId] = useState<string | undefined>(undefined);
  const [lastTargetVarietyId, setLastTargetVarietyId] = useState<string | undefined>(undefined);
  const [lastTargetScriptId, setLastTargetScriptId] = useState<string | undefined>(undefined);

  // Usage limit dialog states
  const [showAnonymousLimitDialog, setShowAnonymousLimitDialog] = useState(false);
  const [showRegisteredLimitDialog, setShowRegisteredLimitDialog] = useState(false);
  const [usageInfo, setUsageInfo] = useState<TranslationCreditLimitInfo | null>(null);
  const [attemptedCharacters, setAttemptedCharacters] = useState(0);

  // History dialog state
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);

  // TODO Starky sense todo list:
  // [x] Starky sense should reset (clear up) after revising
  // [x] It should also disappear if you turn it off
  // [ ] Clear button should clear starky sense too
  // [x] Changing the isdirty state should minimize the sense button to just the icon

  // Starky Sense state
  const [starkySenseResult, setStarkySenseResult] = useState<StarkySenseResult | null>(null);
  const [pendingStarkySense, setPendingStarkySense] = useState(0);
  const [showStarkySenseDialog, setShowStarkySenseDialog] = useState(false);
  const [isStarkySenseEnabled, setIsStarkySenseEnabled] = useState(false);
  const [lastStarkySenseEnabled, setLastStarkySenseEnabled] = useState(false);
  const starkySenseController = useRef<AbortController | null>(null);

  const queryClient = useQueryClient();
  const t = useTranslations('errors');
  const locale = useLocale();
  const searchParams = useSearchParams();

  // Dynamic font sizing hook
  const dynamicFontSize = useDynamicFontSize();

  // Fetch languages for history restoration
  const { data: languages = [] } = useQuery({
    queryKey: ['languages', locale],
    queryFn: () => getLanguages(locale),
    staleTime: 60_000
  });

  const isAutoDetect = () => !sourceLang || sourceLang?.id === 'detect';

  const wasAutoDetect = () => lastSourceLang?.id === 'detect';

  // If comparing, only compare by ID
  function getEffectiveSourceLang(last: boolean = false) {
    const currentLang = last ? lastSourceLang : sourceLang;
    const isAuto = last ? wasAutoDetect() : isAutoDetect();

    return isAuto && transAnalysis?.detectedLanguageId ?
      {
        id: transAnalysis.detectedLanguageId,
        name: transAnalysis.detectedLanguageName
      }
      : currentLang;
  }
  const getLastEffectiveSourceLang = () => getEffectiveSourceLang(true);

  const isTranslating = pendingTranslations > 0;
  const isStarkySenseLoading = pendingStarkySense > 0;
  const exceedsSingleTranslationLimit = inputText.length > SINGLE_TRANSLATION_LIMITS.MAX_CHARACTERS;
  const starkySenseParallelMode = inputText.length < STARKY_SENSE_LIMITS.PARALLEL_MODE_THRESHOLD;
  const hasChanges =
    inputText.trim() !== lastTranslatedText.trim() ||
    (sourceLang?.id !== lastSourceLang?.id &&
      sourceLang?.id !== getLastEffectiveSourceLang()?.id) ||
    targetLang?.id !== lastTargetLang?.id ||
    targetVarietyId !== lastTargetVarietyId ||
    targetScriptId !== lastTargetScriptId ||
    JSON.stringify(translationContext) !== JSON.stringify(lastTranslationContext) ||
    (isStarkySenseEnabled && !lastStarkySenseEnabled) ||
    !!tempImageContext;

  const handleSourceLangChange = (lang: SelectedLanguage) => {
    setSourceLang(lang);
  };

  const handleTargetLangChange = (lang: SelectedLanguage) => {
    setTargetLang(lang);
    // Reset variety and script when language changes
    setTargetVarietyId(undefined);
    setTargetScriptId(undefined);
  };

  const handleTargetVarietyChange = (varietyId: string | undefined) => {
    setTargetVarietyId(varietyId);
  };

  const handleTargetScriptChange = (scriptId: string | undefined) => {
    setTargetScriptId(scriptId);
  };

  const runStarkySense = async (token: string): Promise<StarkySenseResult | null> => {
    if (!isStarkySenseEnabled) {
      return null;
    }

    // Cancel any existing Starky Sense request
    if (starkySenseController.current) {
      starkySenseController.current.abort();
    }

    const controller = new AbortController();
    starkySenseController.current = controller;

    setStarkySenseResult(null);
    setPendingStarkySense(c => c + 1);

    try {
      const requestData: StarkySenseRequest = {
        token: token,
        locale
      };

      const response = await fetch('/api/starky-sense', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
        signal: controller.signal
      });

      if (!response.ok) {
        if (response.status === 403) {
          // Token invalid or expired - silently fail
          console.warn('Starky Sense token invalid or expired');
          return null;
        }
        throw new Error('Starky Sense analysis failed');
      }

      if (!response.body) {
        throw new Error('Response body is empty');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedData = '';

      while (true) {
        const { value, done } = await reader.read();

        if (done)
          break;

        // Accumulate the response data (no SSE parsing needed)
        accumulatedData += decoder.decode(value, { stream: true });
      }

      // Final decode to flush any remaining bytes
      accumulatedData += decoder.decode();

      // Parse the final JSON result
      let finalResult: StarkySenseResult | null = null;
      if (accumulatedData.trim()) {
        try {
          finalResult = JSON.parse(accumulatedData) as StarkySenseResult;
        } catch (parseError) {
          console.error('Failed to parse Starky Sense result:', parseError);
        }
      }

      setStarkySenseResult(finalResult);

      return finalResult;
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Request was aborted
        return null;
      } else {
        console.error('Starky Sense error:', error);
        return null;
      }
    } finally {
      setPendingStarkySense(c => c - 1);
      starkySenseController.current = null;
    }
  };

  const handleTranslateClick = async () => {
    translateText(undefined, undefined, true);
  };

  const translateText = async (forcedSolutions?: StarkySenseSolution[], overrideText?: string, runStarkySenseAfter: boolean = false) => {
    const textToTranslate = overrideText ?? inputText;
    if (textToTranslate.trim() === '' || !targetLang) {
      return;
    }

    setLastTranslatedText(textToTranslate);
    setLastSourceLang(sourceLang);
    setLastTargetLang(targetLang);
    setLastTargetVarietyId(targetVarietyId);
    setLastTargetScriptId(targetScriptId);
    setLastTranslationContext(translationContext);
    setLastStarkySenseEnabled(isStarkySenseEnabled);

    // Cancel any existing request
    if (currentRequestController.current) {
      currentRequestController.current.abort();
    }

    // Create new controller for this request
    currentRequestController.current = new AbortController();
    setPendingTranslations(c => c + 1);

    // Use forced solutions (if any)
    const effectiveSolutions = forcedSolutions || [];

    try {
      // Temporarily merge image context with translation context if present
      const effectiveContext = tempImageContext ?
        { ...translationContext, context: `${translationContext.context || ''}${translationContext.context ? '. ' : ''}Image context: ${tempImageContext}` } :
        translationContext;

      const requestData: TranslateRequest = {
        text: textToTranslate,
        sourceLang: isAutoDetect() ? undefined : sourceLang?.id,
        targetLang: targetLang.id,
        targetVariety: targetVarietyId,
        targetScript: targetScriptId,
        context: effectiveContext,
        starkySenseSolutions: effectiveSolutions,
        locale: locale
      };

      const response = await fetch('api/translate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
        signal: currentRequestController.current.signal
      });

      if (!response.ok) {
        if (response.status === 429) {
          // Handle insufficient credit balance
          try {
            const errorData = await response.json();

            if (errorData.code === 'INSUFFICIENT_CREDITS' && errorData.details) {
              const { details } = errorData;

              setAttemptedCharacters(details.attemptedCharacters);
              setUsageInfo({
                canTranslate: false,
                availableCharacters: details.availableCharacters,
                attemptedCharacters: details.attemptedCharacters,
                insufficientBy: details.insufficientBy,
                isAnonymous: details.isAnonymous,
              });

              if (details.isAnonymous) {
                setShowAnonymousLimitDialog(true);
              } else {
                setShowRegisteredLimitDialog(true);
              }
              return; // Exit early, don't show generic error
            }
          } catch (parseError) {
            console.error('Failed to parse insufficient credit error:', parseError);
          }
        }
        if (response.status === 413) {
          // Handle single translation limit exceeded
          try {
            const errorData = await response.json();

            if (errorData.code === 'SINGLE_TRANSLATION_LIMIT_EXCEEDED') {
              toast.error(`Text too long for translation. Maximum ${SINGLE_TRANSLATION_LIMITS.MAX_CHARACTERS.toLocaleString()} characters allowed.`);
              return; // Exit early, don't show generic error
            }
          } catch (parseError) {
            console.error('Failed to parse single translation limit error:', parseError);
          }
        }
        throw new Error(t('translationFailed'));
      }

      // Extract translation token from response headers
      const translationToken = response.headers.get('X-Translation-Token');

      console.log('Translation token is ready:', translationToken);

      // Start Starky Sense analysis if requested and token is available
      if (runStarkySenseAfter && translationToken && isStarkySenseEnabled) {
        runStarkySense(translationToken);
      }

      if (!response.body) {
        throw new Error(t('responseBodyEmpty'));
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let data = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        data += decoder.decode(value, { stream: true });
      }

      // Final decode to flush any remaining bytes
      data += decoder.decode();

      if (!data.includes('\n//endoftranslation//\n')) {
        throw new Error('Invalid translation stream response');
      }

      // Split response into translation and analysis
      const [translation, analysisJson] = data.split('\n//endoftranslation//\n');

      setOutputText(translation || '');

      // Clear the temporary image context after successful translation
      setTempImageContext(undefined);

      // Process analysis report if available
      let detectedLanguage: { id: string; name: string } | null = null;
      if (analysisJson) {
        try {
          const analysis = JSON.parse(analysisJson) as LanguageExtractionResult;

          if (analysis?.detectedLanguageName && isAutoDetect()) {
            setTransAnalysis(analysis);
            detectedLanguage = {
              id: analysis.detectedLanguageId,
              name: analysis.detectedLanguageName
            };
          }
        } catch (parseError) {
          console.error('Failed to parse analysis data:', parseError);
        }
      }

      queryClient.invalidateQueries({
        queryKey: ['recentLanguages']
      });

      // Dispatch custom event for usage indicator refresh
      window.dispatchEvent(new CustomEvent('translation-complete'));

      // Save translation to history (fire-and-forget)
      // Use detected language if available, otherwise use the effective source language
      const sourceLanguageForHistory = detectedLanguage || getEffectiveSourceLang();
      if (sourceLanguageForHistory?.id && targetLang?.id) {
        historyService.addHistoryEntry({
          sourceLanguageId: sourceLanguageForHistory.id,
          targetLanguageId: targetLang.id,
          sourceText: textToTranslate,
          targetText: translation || '',
          targetVarietyId: targetVarietyId,
          targetScriptId: targetScriptId
        }).catch(error => {
          // Continue silently - history saving failure shouldn't affect translation
          console.error('Failed to save translation to history:', error);
        });

        // Note: No need to invalidate cache here since history query is lazy
        // It will fetch fresh data when the dialog is opened
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Request was aborted
      } else {
        console.error('Translation error:', error);
        setLastTranslatedText(''); // Wasn't really translated, was it                
        toast.error("Translation failed! Please try again later.");
      }
    } finally {
      setPendingTranslations(c => c - 1);
      currentRequestController.current = null;
    }
  };

  // Starky Sense dialog handlers
  const handleStarkySenseRevise = (selectedSolutions: StarkySenseSolution[]) => {
    setShowStarkySenseDialog(false);

    setStarkySenseResult(null);

    // Trigger translation with the selected solutions
    // When revising, we should always use the text that was last translated, as the Starky Sense analysis is based on that.
    // Revise can be done before current translation even finishes.
    translateText(selectedSolutions, lastTranslatedText, false); // Don't run Starky Sense again after revision
  };

  const handleStarkySenseIgnore = () => {
    setShowStarkySenseDialog(false);
    // Continue with translation without Starky Sense solutions
    if (!starkySenseParallelMode) {
      translateText([], undefined, false); // Don't run Starky Sense again
    }
  };

  const handleShowStarkySenseDialog = () => {
    setShowStarkySenseDialog(true);
  };

  useEffect(() => {
    if (hasChanges) {
      // Starky sense button is now minimized when there are changes
    }
  }, [hasChanges]);

  useEffect(() => {
    const handleCheckoutPrepare = (event: Event) => {
      const customEvent = event as CustomEvent<CheckoutPrepareRedirectDetail>;
      const restoreKey = sanitizeRestoreKey(customEvent.detail?.restoreKey);

      if (!restoreKey) {
        return;
      }

      const payload: CheckoutDraftPayload = {
        version: 1,
        savedAt: Date.now(),
        inputText,
        sourceLang: sourceLang ?? null,
        targetLang: targetLang ?? null,
        targetVarietyId: targetVarietyId ?? null,
        targetScriptId: targetScriptId ?? null,
        translationContext,
        tempImageContext: tempImageContext ?? null,
      };

      pruneExpiredCheckoutDrafts();
      localStorage.setItem(getCheckoutDraftStorageKey(restoreKey), JSON.stringify(payload));
    };

    window.addEventListener(CHECKOUT_PREPARE_REDIRECT_EVENT, handleCheckoutPrepare as EventListener);

    return () => {
      window.removeEventListener(CHECKOUT_PREPARE_REDIRECT_EVENT, handleCheckoutPrepare as EventListener);
    };
  }, [inputText, sourceLang, targetLang, targetVarietyId, targetScriptId, translationContext, tempImageContext]);

  useEffect(() => {
    pruneExpiredCheckoutDrafts();

    const checkoutStatus = searchParams.get('checkout');
    const restoreKey = sanitizeRestoreKey(searchParams.get('restore'));

    if (checkoutStatus !== 'success' || !restoreKey) {
      return;
    }

    const draftStorageKey = getCheckoutDraftStorageKey(restoreKey);
    const rawDraft = localStorage.getItem(draftStorageKey);

    if (!rawDraft) {
      return;
    }

    try {
      const draft = JSON.parse(rawDraft) as CheckoutDraftPayload;

      if (Date.now() - draft.savedAt > CHECKOUT_DRAFT_TTL_MS) {
        localStorage.removeItem(draftStorageKey);
        return;
      }

      setInputText(draft.inputText ?? '');
      setSourceLang(draft.sourceLang ?? undefined);
      setTargetLang(draft.targetLang ?? undefined);
      setTargetVarietyId(draft.targetVarietyId ?? undefined);
      setTargetScriptId(draft.targetScriptId ?? undefined);
      setTranslationContext(draft.translationContext ?? {});
      setTempImageContext(draft.tempImageContext ?? undefined);

      dynamicFontSize.setTextWithOptimalFont(draft.inputText ?? '', outputText);
      localStorage.removeItem(draftStorageKey);

      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('checkout');
      currentUrl.searchParams.delete('restore');
      window.history.replaceState({}, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
    } catch {
      localStorage.removeItem(draftStorageKey);
    }
  }, [dynamicFontSize, outputText, searchParams]);

  // Clear Starky Sense result when disabled
  useEffect(() => {
    if (!isStarkySenseEnabled) {
      setStarkySenseResult(null);
      // Also abort any ongoing Starky Sense request
      if (starkySenseController.current) {
        starkySenseController.current.abort();
      }
    }
  }, [isStarkySenseEnabled]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (currentRequestController.current) {
        currentRequestController.current.abort();
      }
      if (starkySenseController.current) {
        starkySenseController.current.abort();
      }
    };
  }, []);

  // Listen for history dialog open events and Starky Sense toggle events
  useEffect(() => {
    const handleOpenHistory = () => {
      setShowHistoryDialog(true);
    };

    const handleStarkySenseToggle = (event: CustomEvent<{ isEnabled: boolean }>) => {
      setIsStarkySenseEnabled(event.detail.isEnabled);
    };

    window.addEventListener('open-translation-history', handleOpenHistory);
    window.addEventListener('starky-sense-toggled', handleStarkySenseToggle as EventListener);

    return () => {
      window.removeEventListener('open-translation-history', handleOpenHistory);
      window.removeEventListener('starky-sense-toggled', handleStarkySenseToggle as EventListener);
    };
  }, []);

  const handleSwitchLanguages = async () => {
    const effectiveSourceLang = (isAutoDetect() && transAnalysis) ?
      {
        id: transAnalysis.detectedLanguageId,
        name: transAnalysis.detectedLanguageName
      } as SelectedLanguage : sourceLang;

    if (!effectiveSourceLang)
      return;

    const newSourceLang = targetLang;
    const newTargetLang = getEffectiveSourceLang();

    setSourceLang(newSourceLang);
    setTargetLang(newTargetLang);

    if (outputText.trim() !== '') {
      setInputText(outputText);
    }

    setOutputText('');

    // Update user settings to persist the language switch
    try {
      await updateUserSettings('languageDropdown-in', { lastUsedLanguage: newSourceLang?.id })
      await updateUserSettings('languageDropdown-out', { lastUsedLanguage: newTargetLang?.id })
      queryClient.invalidateQueries({ queryKey: ['settings', 'languageDropdown-in'] });
      queryClient.invalidateQueries({ queryKey: ['settings', 'languageDropdown-out'] });
    } catch (error) {
      console.error('Failed to update language settings:', error);
    }
  }

  function handleClearText(undoClear: boolean): void {
    if (undoClear) {
      setOutputText(lastTranslatedText);
    }
    else {
      setOutputText('');
      setStarkySenseResult(null);
      if (starkySenseController.current) {
        starkySenseController.current.abort();
      }
    }
  }

  const handleTextExtracted = (text: string, context?: string) => {
    // Pre-calculate font size before setting text to prevent jumpiness
    dynamicFontSize.setTextWithOptimalFont(text, undefined);

    // Add extracted text to input
    //setInputText(inputText + (inputText ? '\n\n' : '') + text);
    setInputText(text); // TODO: Maybe a warning if there was some previous text?

    // Set temporary context for this translation only
    if (context) {
      setTempImageContext(context);
    }

    // Auto-trigger translation if we have a target language
    if (targetLang && text.trim()) {
      // Small delay to let the text update
      setTimeout(() => {
        //translateText();
      }, 100);
    }
  };

  const handleHistorySelection = (entry: HistoryEntry) => {
    // Find the complete language data from the languages list
    const sourceLanguageData = languages.find(lang => lang.id === entry.sourceLanguageId);
    const targetLanguageData = languages.find(lang => lang.id === entry.targetLanguageId);

    // Set the source and target languages with complete data
    const sourceLang: SelectedLanguage = sourceLanguageData ? {
      id: sourceLanguageData.id,
      name: sourceLanguageData.name,
      default_variety_id: sourceLanguageData.default_variety_id,
      default_script_id: sourceLanguageData.default_script_id
    } : { id: entry.sourceLanguageId, name: entry.sourceLangName };

    const targetLang: SelectedLanguage = targetLanguageData ? {
      id: targetLanguageData.id,
      name: targetLanguageData.name,
      default_variety_id: targetLanguageData.default_variety_id,
      default_script_id: targetLanguageData.default_script_id
    } : { id: entry.targetLanguageId, name: entry.targetLangName };

    setSourceLang(sourceLang);
    setTargetLang(targetLang);

    // Pre-calculate optimal font size before setting text to prevent jumpiness
    dynamicFontSize.setTextWithOptimalFont(entry.sourceText, entry.targetText);

    // Set the texts
    setInputText(entry.sourceText);
    setOutputText(entry.targetText);

    // Set variety and script - these will be applied once the dropdowns load
    if (entry.targetVarietyId)
      setTargetVarietyId(entry.targetVarietyId);
    if (entry.targetScriptId)
      setTargetScriptId(entry.targetScriptId);

    // Clear any previous translation states
    setLastTranslatedText('');
    setLastSourceLang(undefined);
    setLastTargetLang(undefined);
    setLastTargetVarietyId(undefined);
    setLastTargetScriptId(undefined);
    setLastStarkySenseEnabled(false);
    setTransAnalysis(null);
  };

  return (
    <ConsentModal>
      <RTLProvider
        inputLanguageId={getEffectiveSourceLang()?.id}
        outputLanguageId={targetLang?.id}
      >
        <main className="container flex flex-1 flex-col p-0 pt-2 md:p-4">
          <div className="grid md:gap-2 grid-rows-[auto,1fr,1fr] md:grid-rows-[auto,1fr] md:grid-cols-[1fr,1fr] flex-1">
            <div className="justify-center items-center md:col-span-2 pb-2 md:pb-0 px-2 md:px-0">
              <div className="grid gap-2 gap-x-1 grid-cols-[1fr,auto,1fr] grid-rows-1 auto-rows-auto">
                <div>
                  <LangDropdown
                    canAutoDetect={true}
                    settingKey="in"
                    title={langT('sourceLanguage')}
                    selectedLangId={sourceLang?.id}
                    onLanguageSelected={handleSourceLangChange}
                    detectedLanguage={
                      transAnalysis?.detectedLanguageId &&
                        isAutoDetect() &&
                        !hasChanges &&
                        !currentRequestController.current
                        ? { name: transAnalysis.detectedLanguageName }
                        : null
                    }
                  />
                </div>
                <div>
                  <Button
                    variant="outline"
                    aria-label={langT('switchLanguages')}
                    className="px-1"
                    onClick={handleSwitchLanguages}
                    disabled={
                      !targetLang ||
                      sourceLang === targetLang ||
                      (isAutoDetect() &&
                        (hasChanges ||
                          !transAnalysis?.detectedLanguageId ||
                          !transAnalysis?.detectedLanguageExisted))
                    }>
                    <ArrowRightLeft />
                  </Button>
                </div>
                <div className="flex items-start gap-2">
                  <LangDropdown
                    settingKey="out"
                    title={langT('targetLanguage')}
                    selectedLangId={targetLang?.id}
                    dropdownXOffset={-13}
                    onLanguageSelected={handleTargetLangChange}
                    className="flex-1" />

                  <LanguageVarietyScriptPicker
                    language={targetLang}
                    selectedVarietyId={targetVarietyId}
                    selectedScriptId={targetScriptId}
                    onVarietyChange={handleTargetVarietyChange}
                    onScriptChange={handleTargetScriptChange}
                  />
                </div>
              </div>
            </div>

            <InputBox
              inputText={inputText}
              setInputText={setInputText}
              isBusy={isTranslating}
              lang={getEffectiveSourceLang()}
              onTranslate={handleTranslateClick}
              onClear={handleClearText}
              context={translationContext}
              setContext={setTranslationContext}
              onTextExtracted={handleTextExtracted}
              dynamicFontSize={dynamicFontSize}
            />

            <OutputBox
              outputText={outputText}
              isBusy={isTranslating}
              lang={lastTargetLang}
              onTranslate={handleTranslateClick}
              showTranslateButton={!!(hasChanges && targetLang && inputText.trim() !== '' && !exceedsSingleTranslationLimit)}
              dynamicFontSize={dynamicFontSize}
              starkySenseResult={starkySenseResult}
              isStarkySenseLoading={isStarkySenseLoading}
              onShowStarkySenseDialog={handleShowStarkySenseDialog}
              hasChanges={hasChanges}
            />

          </div>

          {/* Debug Usage Panel */}
          {/* <div className="mt-4 px-2 md:px-0">
            <DebugUsagePanel inputText={inputText} />
          </div> */}

          {/* Usage Limit Dialogs */}
          {usageInfo && (
            <>
              <AnonymousLimitDialog
                isOpen={showAnonymousLimitDialog}
                onClose={() => setShowAnonymousLimitDialog(false)}
                availableCharacters={usageInfo.availableCharacters}
                attemptedCharacters={usageInfo.attemptedCharacters || attemptedCharacters}
                insufficientBy={usageInfo.insufficientBy}
              />
              <RegisteredLimitDialog
                isOpen={showRegisteredLimitDialog}
                onClose={() => setShowRegisteredLimitDialog(false)}
                availableCharacters={usageInfo.availableCharacters}
                attemptedCharacters={usageInfo.attemptedCharacters || attemptedCharacters}
                insufficientBy={usageInfo.insufficientBy}
              />
            </>
          )}
        </main>
      </RTLProvider>

      {/* Install prompt - only shows after consent is given */}
      <InstallPrompt />

      {/* Translation History Dialog */}
      <TranslationHistory
        isOpen={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
        onSelectEntry={handleHistorySelection}
      />

      {/* Starky Sense Dialog */}
      <StarkySenseDialog
        isOpen={showStarkySenseDialog}
        onClose={() => setShowStarkySenseDialog(false)}
        onRevise={handleStarkySenseRevise}
        onIgnore={handleStarkySenseIgnore}
        result={starkySenseResult}
        targetLanguage={targetLang?.name || ''}
      />
    </ConsentModal>
  );
}